<template>
  <AdminLayout :page-title="isEdit ? 'Edit User' : 'New User'">
    <template #header-actions>
      <div class="hidden sm:flex items-center gap-3">
        <button type="button" @click="handleCancel" class="dashboard-secondary-btn">
          Cancel
        </button>
        <button
          type="button"
          :disabled="saving || loadingUser"
          class="dashboard-primary-btn"
          @click="handleSave"
        >
          {{ saving ? "Saving..." : isEdit ? "Save Changes" : "Create User" }}
        </button>
      </div>
    </template>

    <div class="mx-auto max-w-2xl">
      <div v-if="loadingUser" class="text-center py-12 text-on-surface-variant">
        Loading user...
      </div>

      <form
        v-else
        @submit.prevent="handleSave"
        class="dashboard-card bg-surface rounded-xl border border-outline-variant/30 overflow-hidden"
      >
        <div class="p-6 space-y-6">
          <div>
            <label class="field-label" for="user-email">Email *</label>
            <input
              id="user-email"
              v-model="formData.email"
              type="email"
              required
              autocomplete="email"
              class="field-input"
              placeholder="user@brandintegrated.com"
            />
          </div>

          <div>
            <label class="field-label" for="user-name">Full Name</label>
            <input
              id="user-name"
              v-model="formData.fullName"
              type="text"
              autocomplete="name"
              class="field-input"
              placeholder="Jane Doe"
            />
          </div>

          <div v-if="!isEdit">
            <label class="field-label" for="user-password">Password *</label>
            <input
              id="user-password"
              v-model="formData.password"
              type="password"
              required
              autocomplete="new-password"
              class="field-input"
              placeholder="Minimum 6 characters"
            />
          </div>

          <div v-if="!isEdit">
            <label class="field-label" for="user-password-confirm">Confirm Password *</label>
            <input
              id="user-password-confirm"
              v-model="formData.confirmPassword"
              type="password"
              required
              autocomplete="new-password"
              class="field-input"
            />
            <p v-if="passwordMismatch" class="text-sm text-error mt-2">
              Passwords do not match
            </p>
          </div>

          <div v-if="isEdit" class="flex items-center justify-between rounded-lg border border-outline-variant/30 p-4">
            <div>
              <p class="text-sm font-medium text-on-surface">Account Status</p>
              <p class="text-xs text-on-surface-variant mt-1">
                Inactive users cannot log in
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="formData.isActive"
                type="checkbox"
                class="sr-only peer"
                :disabled="isSelf && formData.isActive"
              />
              <div
                class="w-11 h-6 bg-outline-variant/40 rounded-full peer peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/30 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
              ></div>
            </label>
          </div>
          <p v-if="isEdit && isSelf" class="text-xs text-on-surface-variant -mt-4">
            You cannot deactivate your own account
          </p>

          <div v-if="isEdit" class="border-t border-outline-variant/30 pt-6">
            <h3 class="text-sm font-medium text-on-surface mb-4">Set New Password (optional)</h3>
            <div class="space-y-4">
              <div>
                <label class="field-label" for="user-new-password">New Password</label>
                <input
                  id="user-new-password"
                  v-model="formData.password"
                  type="password"
                  autocomplete="new-password"
                  class="field-input"
                  placeholder="Leave blank to keep current password"
                />
              </div>
              <div>
                <label class="field-label" for="user-new-password-confirm">Confirm New Password</label>
                <input
                  id="user-new-password-confirm"
                  v-model="formData.confirmPassword"
                  type="password"
                  autocomplete="new-password"
                  class="field-input"
                />
                <p v-if="passwordMismatch" class="text-sm text-error mt-2">
                  Passwords do not match
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="sm:hidden flex items-center justify-end gap-3 px-6 py-4 border-t border-outline-variant/30">
          <button type="button" @click="handleCancel" class="dashboard-secondary-btn flex-1">
            Cancel
          </button>
          <button type="submit" :disabled="saving" class="dashboard-primary-btn flex-1">
            {{ saving ? "Saving..." : isEdit ? "Save" : "Create" }}
          </button>
        </div>
      </form>
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import AdminLayout from "../../components/admin/AdminLayout.vue";
import { authService } from "../../utils/auth.js";
import {
  getAdminUserById,
  createAdminUser,
  updateAdminUser,
  updateAdminUserPassword,
} from "../../utils/users.js";
import { showAdminToast } from "../../utils/adminToast.js";

const route = useRoute();
const router = useRouter();

const isEdit = computed(() => !!route.params.id);
const loadingUser = ref(!!route.params.id);
const saving = ref(false);

const currentUserId = authService.getCurrentUser()?.userId;
const isSelf = computed(() => isEdit.value && route.params.id === currentUserId);

const formData = ref({
  email: "",
  fullName: "",
  password: "",
  confirmPassword: "",
  isActive: true,
});

const passwordMismatch = computed(() => {
  if (!formData.value.password && !formData.value.confirmPassword) return false;
  return formData.value.password !== formData.value.confirmPassword;
});


const loadUser = async () => {
  if (!isEdit.value) return;

  loadingUser.value = true;
  try {
    const user = await getAdminUserById(route.params.id);
    if (!user) {
      showAdminToast("User not found", "error");
      router.push("/admin/users");
      return;
    }

    formData.value = {
      email: user.email,
      fullName: user.fullName,
      password: "",
      confirmPassword: "",
      isActive: user.isActive,
    };
  } catch (error) {
    console.error("Error loading user:", error);
    showAdminToast("Error loading user", "error");
  } finally {
    loadingUser.value = false;
  }
};

const handleSave = async () => {
  if (passwordMismatch.value) {
    showAdminToast("Passwords do not match", "error");
    return;
  }

  saving.value = true;
  try {
    if (isEdit.value) {
      await updateAdminUser(route.params.id, {
        email: formData.value.email,
        fullName: formData.value.fullName,
        isActive: formData.value.isActive,
      });

      if (formData.value.password) {
        await updateAdminUserPassword(route.params.id, formData.value.password);
      }

      sessionStorage.setItem(
        "adminFlash",
        JSON.stringify({ message: "User updated successfully!", type: "success" })
      );
    } else {
      if (!formData.value.password) {
        showAdminToast("Password is required", "error");
        saving.value = false;
        return;
      }

      await createAdminUser({
        email: formData.value.email,
        fullName: formData.value.fullName,
        password: formData.value.password,
      });

      sessionStorage.setItem(
        "adminFlash",
        JSON.stringify({ message: "User created successfully!", type: "success" })
      );
    }

    router.push("/admin/users");
  } catch (error) {
    console.error("Error saving user:", error);
    showAdminToast(error.message || "Error saving user. Please try again.", "error");
  } finally {
    saving.value = false;
  }
};

const handleCancel = () => {
  router.push("/admin/users");
};

onMounted(async () => {
  if (!authService.isAuthenticated()) {
    router.push("/admin/login");
    return;
  }

  if (isEdit.value && !isSelf.value) {
    sessionStorage.setItem(
      "adminFlash",
      JSON.stringify({ message: "You can only edit your own profile", type: "error" })
    );
    router.push("/admin/users");
    return;
  }

  await loadUser();
});
</script>

<style scoped>
.dashboard-card {
  box-shadow: 0 10px 30px rgba(21, 66, 18, 0.08);
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-on-surface, #1b1c1c);
  margin-bottom: 0.5rem;
}

.field-input {
  width: 100%;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(194, 201, 187, 0.5);
  background: var(--color-surface, #fbf9f8);
  color: var(--color-on-surface, #1b1c1c);
}

.field-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary, #154212);
}

.dashboard-primary-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
}

.dashboard-primary-btn:hover:not(:disabled) {
  background-color: #2d5a27;
  transform: translateY(-1px);
}

.dashboard-primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dashboard-secondary-btn {
  padding: 0.5rem 1.1rem;
  border-radius: 9999px;
  border: 1px solid rgba(21, 66, 18, 0.2);
  background: rgba(255, 255, 255, 0.9);
  color: #154212;
  font-weight: 500;
  font-size: 0.875rem;
}

.dashboard-secondary-btn:hover {
  background: #f5f3f3;
  border-color: #154212;
}
</style>
